import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  Alert
} from '@mui/material';
import {
  Close,
  CheckCircle,
  Cancel,
  Warning,
  Schedule,
  Person,
  LocalCafe,
  Receipt
} from '@mui/icons-material';
import { useOrders } from '../context/OrdersContext';
import UnavailableItemsModal from './UnavailableItemsModal';
import { formatMoscowTime, formatMoscowDateTime } from '../utils/timeUtils';
import { orderDisplayNumber } from '../utils/orderDisplay';

const OrderDetailsModal = ({ order, open, onClose }) => {
  const { confirmOrder, rejectOrder, completeOrder, returnToAwaitingConfirmation, updateOrderStatus } = useOrders();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showUnavailableItemsModal, setShowUnavailableItemsModal] = useState(false);

  useEffect(() => {
    if (open) {
      setIsProcessing(false);
      setShowUnavailableItemsModal(false);
    }
  }, [open, order?.id]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
      case 'new':
        return 'error';
      case 'paid':
        return 'info';
      case 'awaiting_confirmation':
        return 'warning';
      case 'confirmed':
      case 'preparing':
        return 'warning';
      case 'ready':
        return 'success';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
      case 'new':
        return 'Новый';
      case 'paid':
        return 'Оплачен';
      case 'awaiting_confirmation':
        return 'Ожидает ответа';
      case 'confirmed':
        return 'Ожидает оплаты';
      case 'preparing':
        return 'Готовится';
      case 'ready':
        return 'Готов';
      case 'completed':
        return 'Выполнен';
      case 'cancelled':
        return 'Отменен';
      default:
        return status;
    }
  };

  // Используем утилиты для форматирования московского времени
  const formatTime = formatMoscowTime;
  const formatDateTime = formatMoscowDateTime;

  const handleConfirm = async () => {
    if (!order || isProcessing) return;
    
    setIsProcessing(true);
    try {
      await confirmOrder(order.id);
      onClose();
    } catch (error) {
      console.error('Ошибка подтверждения заказа:', error);
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!order || isProcessing) return;
    
    setIsProcessing(true);
    try {
      await rejectOrder(order.id);
      onClose();
    } catch (error) {
      console.error('Ошибка отмены заказа:', error);
      setIsProcessing(false);
    }
  };

  const handleComplete = async () => {
    if (!order || isProcessing) return;
    
    setIsProcessing(true);
    try {
      await completeOrder(order.id);
      onClose();
    } catch (error) {
      console.error('Ошибка завершения заказа:', error);
      setIsProcessing(false);
    }
  };

  const handleMarkAsCompleted = async () => {
    if (!order || isProcessing) return;
    
    setIsProcessing(true);
    try {
      await updateOrderStatus(order.id, 'completed');
      onClose();
    } catch (error) {
      console.error('Ошибка отметки заказа как выданного:', error);
      setIsProcessing(false);
    }
  };

  const handleRequestChanges = () => {
    if (!order) return;
    setShowUnavailableItemsModal(true);
  };

  const handleSubmitUnavailableItems = async (unavailableItemsDescription) => {
    if (!order) return;
    
    setIsProcessing(true);
    try {
      await returnToAwaitingConfirmation(order.id, unavailableItemsDescription);
      console.log(`[OrderDetailsModal] Заказ #${order.id} отправлен на изменение`);
      setShowUnavailableItemsModal(false);
      onClose();
    } catch (error) {
      console.error('Ошибка отправки на изменение:', error);
      setIsProcessing(false);
      throw error;
    }
  };

  const handleCancelUnavailableItems = () => {
    setShowUnavailableItemsModal(false);
  };

  if (!order) return null;

  const handleStartPreparing = async () => {
    if (!order || isProcessing) return;
    
    setIsProcessing(true);
    try {
      await updateOrderStatus(order.id, 'preparing');
      onClose();
    } catch (error) {
      console.error('Ошибка изменения статуса заказа на "Готовится":', error);
      setIsProcessing(false);
    }
  };

  const renderActions = () => {
    const status = order.status;

    if (status === 'new' || status === 'pending') {
      return (
        <>
          <Button
            onClick={handleReject}
            variant="outlined"
            color="error"
            size="large"
            startIcon={<Cancel />}
            disabled={isProcessing}
          >
            Отменить
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="success"
            size="large"
            startIcon={<CheckCircle />}
            disabled={isProcessing}
          >
            {isProcessing ? 'Подтверждение...' : 'Принять заказ'}
          </Button>
        </>
      );
    }

    if (status === 'paid') {
      return (
        <>
          <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
            💳 Заказ оплачен, можно начать готовить
          </Alert>
          <Button
            onClick={handleStartPreparing}
            variant="contained"
            size="large"
            fullWidth
            disabled={isProcessing}
            sx={{
              bgcolor: 'rgb(33, 150, 243)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgb(25, 118, 210)'
              }
            }}
          >
            {isProcessing ? 'Обработка...' : 'Начать готовить'}
          </Button>
        </>
      );
    }

    if (status === 'confirmed') {
      return (
        <>
          <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
            💳 Ожидание оплаты — выдавать заказ можно только после оплаты клиентом
          </Alert>
          <Button
            onClick={handleRequestChanges}
            variant="outlined"
            color="warning"
            size="large"
            fullWidth
            startIcon={<Warning />}
            disabled={isProcessing}
          >
            Отправить на изменение
          </Button>
        </>
      );
    }

    if (status === 'preparing') {
      return (
        <>
          <Button
            onClick={handleRequestChanges}
            variant="outlined"
            color="warning"
            size="large"
            startIcon={<Warning />}
            disabled={isProcessing}
          >
            Отправить на изменение
          </Button>
          <Button
            onClick={handleComplete}
            variant="contained"
            color="success"
            size="large"
            startIcon={<CheckCircle />}
            disabled={isProcessing}
          >
            {isProcessing ? 'Обработка...' : 'Заказ готов'}
          </Button>
        </>
      );
    }

    if (status === 'awaiting_confirmation') {
      return (
        <Alert severity="info" sx={{ width: '100%' }}>
          Заказ ожидает ответа клиента
        </Alert>
      );
    }

    if (status === 'ready') {
      return (
        <Button
          onClick={handleMarkAsCompleted}
          variant="contained"
          color="success"
          size="large"
          fullWidth
          startIcon={<CheckCircle />}
          disabled={isProcessing}
        >
          {isProcessing ? 'Обработка...' : 'Выдан'}
        </Button>
      );
    }

    if (status === 'completed') {
      return (
        <Alert severity="info" sx={{ width: '100%' }}>
          Заказ выполнен
        </Alert>
      );
    }

    if (status === 'cancelled') {
      return (
        <Alert severity="error" sx={{ width: '100%' }}>
          Заказ отменен
        </Alert>
      );
    }

    return null;
  };

  return (
    <>
      <Dialog
        open={open && !showUnavailableItemsModal}
        onClose={isProcessing ? undefined : onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Receipt sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h6">
                Заказ #{orderDisplayNumber(order)}
              </Typography>
              <Chip
                label={getStatusText(order.status)}
                color={getStatusColor(order.status)}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={isProcessing}
            sx={{ color: 'inherit' }}
          >
            <Close />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box mb={2}>
            <Box display="flex" alignItems="center" mb={1}>
              <Schedule sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Создан: <strong>{formatDateTime(order.created_at)}</strong>
              </Typography>
            </Box>

            {order.estimated_ready_time && (
              <Box display="flex" alignItems="center" mb={2}>
                <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1">
                  Готовность к: <strong>{formatTime(order.estimated_ready_time)}</strong>
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" alignItems="center" mb={1}>
            <LocalCafe sx={{ mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Состав заказа:
            </Typography>
          </Box>

          <List dense>
            {order.items && order.items.length > 0 ? (
              order.items.map((item, index) => {
                const itemName = item.item_name || item.name || 'Позиция';
                const sizeName = item.size_name || '';
                const hasAddons = item.addons && item.addons.length > 0;
                
                return (
                  <Box
                    key={item.id || index}
                    sx={{
                      py: 1.5,
                      borderBottom: index < order.items.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider'
                    }}
                  >
                    {/* Основная позиция */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1" fontWeight="medium">
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            minWidth: '40px',
                            color: 'primary.main',
                            fontWeight: 'bold'
                          }}
                        >
                          {item.quantity || 1}x
                        </Box>
                        {itemName}{sizeName ? ` (${sizeName})` : ''}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {item.price_at_time || 0} ₽
                      </Typography>
                    </Box>
                    
                    {/* Добавки */}
                    {hasAddons && (
                      <Box sx={{ ml: 5, mt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                          Дополнительно:
                        </Typography>
                        {item.addons.map((addon, addonIndex) => (
                          <Box 
                            key={addonIndex} 
                            display="flex" 
                            justifyContent="space-between"
                            sx={{ ml: 1, mb: 0.5 }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              + {addon.addon_name || addon.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              +{addon.price_at_time || 0} ₽
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                Нет позиций в заказе
              </Typography>
            )}
          </List>

          {/* Скидка (промокод) */}
          {order.discount_amount != null && Number(order.discount_amount) > 0 && (
            <Box mt={2} pt={2} borderTop={1} borderColor="divider">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" color="text.secondary">
                  Скидка (промокод)
                </Typography>
                <Typography variant="body1" sx={{ color: 'success.main' }}>
                  −{Number(order.discount_amount).toFixed(2)} ₽
                </Typography>
              </Box>
            </Box>
          )}

          {/* Сервисный сбор */}
          {order.service_commission && order.service_commission > 0 && (
            <Box mt={2} pt={2} borderTop={1} borderColor="divider">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" color="text.secondary">
                  Сервисный сбор
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {order.service_commission} ₽
                </Typography>
              </Box>
            </Box>
          )}

          {/* Итого */}
          {order.total_amount && (
            <Box mt={order.service_commission ? 1 : 2} pt={order.service_commission ? 2 : 2} borderTop={2} borderColor="divider">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                  Итого:
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {order.total_amount} ₽
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          {renderActions()}
        </DialogActions>
      </Dialog>

      <UnavailableItemsModal
        order={order}
        open={showUnavailableItemsModal}
        onSubmit={handleSubmitUnavailableItems}
        onCancel={handleCancelUnavailableItems}
      />
    </>
  );
};

export default OrderDetailsModal;






